import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import {
  expandPlanDataParameterValuesForRest,
  expandPlanMaterialInputValuesForRest,
} from 'procedures/planning/multiValuePlateMap';
import {ExperimentalInputs, FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  ProcedureRunPlanDesignStateSummary,
  setProcedureRunPlanDesignState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {Condition} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {withLoadingScreen} from 'util/Promises';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunPlanStudyDesignTabContentsQuery,
  MultiProcedureRunPlanStudyDesignTabContentsQueryVariables,
  MultiProcedureRunPlanStudyDesignTabContentsQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
} from './__generated/MultiProcedureRunPlanStudyDesignTabContentsQuery';
import EditProcedureModal, {EditProcedureModalMode, ProcedureChangeParams} from './EditProcedureModal';
import getStudyDesignFromProcedureRunPlan, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT,
} from './getStudyDesignFromProcedureRunPlan';
import {StudyOverviewContext} from './MultiProcedureRunAndPlanOverviewTabContents';
import {preserveProcedureInputsOnVersionUpdate} from './preserveProcedureInputsOnVersionUpdate';
import {
  collectPlanErrors,
  getConditionValidationTooltip,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import PublishedProcedureVersionUpdateBanner from './PublishedProcedureVersionUpdateBanner';
import SelectedProcedureEditor from './SelectedProcedureEditor';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import {useProcedureVersionUpdateCheck} from './useProcedureVersionUpdateCheck';
import {useStudyDesignState} from './useStudyDesignState';

// Indices for accessing typed values in the experiment plan
const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;

const AMOUNT_INDEX = 1;

function hasCompatiblePersistedMethodInputs(
  experimentalInputs: ExperimentalInputs,
  methodIds: Set<string>
): boolean {
  const persistedMethodIds = Object.keys(experimentalInputs);
  // Require an exact key-set match so that we never reuse persisted inputs
  // that are missing entries for current methods or contain obsolete ones.
  if (persistedMethodIds.length !== methodIds.size) {
    return false;
  }
  return persistedMethodIds.every((methodId) => methodIds.has(methodId));
}

function isCompatiblePersistedPlanDesignState(
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined,
  methodIds: Set<string>
): persistedPlanDesignState is ProcedureRunPlanDesignStateSummary {
  return (
    persistedPlanDesignState != null &&
    hasCompatiblePersistedMethodInputs(persistedPlanDesignState.experimentalInputs, methodIds)
  );
}

/**
 * Serializes experiment plan conditions to the procedure run PLAN REST format.
 * Note: This is different from serializeExperimentConditionsForNetwork which is for procedure RUNS.
 *
 * Plan REST API expects {value: TypedValue} for all types (data params, material inputs, equipment).
 * For material inputs, we nest both material and amount inside the value object.
 *
 * For parameters confirmed via plate map, the UI represents multiple setpoints as a single
 * multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK). This function expands those into
 * multiple `values[]` entries with one scalar TypedValue each, matching the contract of
 * serializeExperimentConditionsForNetwork and the backend planned-value upsert shape.
 */
export function serializePlanConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan,
  conditions: Array<Condition>
): Array<ProcedureRunPlanConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;

  return experimentPlanPerCondition.map((methodPlans, i) => ({
    // Only include id for existing conditions; omit for new conditions so backend creates them
    ...(conditions[i].id != null ? {id: conditions[i].id} : {}),
    name: namesPerCondition[i],
    description: descriptionsPerCondition[i],
    position: i,
    replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
      replicateNumber: j + 1,
    })),
    methodPlans: methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: expandPlanDataParameterValuesForRest(pv.values[VALUE_INDEX]),
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        values: expandPlanMaterialInputValuesForRest(pv.values[MATERIAL_INDEX], pv.values[AMOUNT_INDEX]),
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
        // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
        // than a missing key.
        values: [{value: {equipment: pv.values[VALUE_INDEX] ?? null}}],
      })),
    })),
  }));
}

/**
 * Serializes preserved experimental inputs directly to REST format for version updates.
 * This bypasses buildFullExperimentPlan which requires evaluatedValues with matching method IDs.
 *
 * @param preservedInputs - The experimental inputs with new method IDs and preserved values
 * @param conditions - The current conditions from the plan
 * @returns Array of conditions in REST format ready for the API
 */
function serializePreservedInputsForNetwork(
  preservedInputs: ExperimentalInputs,
  conditions: Array<Condition>
): Array<ProcedureRunPlanConditionREST> {
  return conditions.map((condition, conditionIdx) => ({
    ...(condition.id != null ? {id: condition.id} : {}),
    name: condition.name,
    description: condition.description,
    position: conditionIdx,
    replicateInfos: Array.from({length: condition.numReplicates}, (_, j) => ({
      replicateNumber: j + 1,
    })),
    methodPlans: Object.entries(preservedInputs).map(([methodId, inputsForMethod]) => {
      const valuesForCondition = inputsForMethod.values[conditionIdx];
      return {
        methodVersionId: methodId,
        dataParameterValues: inputsForMethod.ids.parameters.map((paramVersionId, paramIdx) => ({
          dataParameterVersionId: paramVersionId,
          values: expandPlanDataParameterValuesForRest(
            valuesForCondition?.parameters[paramIdx]?.[VALUE_INDEX]
          ),
        })),
        materialInputValues: inputsForMethod.ids.materialInputs.map((materialVersionId, materialIdx) => ({
          materialInputVersionId: materialVersionId,
          values: expandPlanMaterialInputValuesForRest(
            valuesForCondition?.materialInputs[materialIdx]?.[MATERIAL_INDEX],
            valuesForCondition?.materialInputs[materialIdx]?.[AMOUNT_INDEX]
          ),
        })),
        equipmentValues: inputsForMethod.ids.equipment.map((equipmentVersionId, equipmentIdx) => ({
          equipmentVersionId,
          // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
          // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
          // than a missing key.
          values: [
            {
              value: {
                equipment: valuesForCondition?.equipment[equipmentIdx]?.[VALUE_INDEX] ?? null,
              },
            },
          ],
        })),
      };
    }),
  }));
}

export const MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanStudyDesignTabContentsQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...GetStudyDesignFromProcedureRunPlanGraphQL
      ... on ProcedureRunPlanNode {
        id
        dataEntryMode
        procedureRunPlanConditions {
          id
          name
          description
          position
          replicates {
            replicateNumber
          }
        }
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT}
`;

function MultiProcedureRunPlanStudyDesignTabContents(props: {
  procedureRunPlan: ProcedureRunPlanNode;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const {procedureRunPlan, procedureType} = props;
  const persistedPlanDesignState = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[props.studyId]?.procedureRunPlanDesignStateByPlanId[
        procedureRunPlan.id
      ]
  );

  const {experimentalInputs: serverInitialExperimentalInputs, templateFlowchart} = useMemo(
    () => getStudyDesignFromProcedureRunPlan(procedureRunPlan),
    [procedureRunPlan]
  );
  const templateMethodIds = useMemo(
    () => new Set(templateFlowchart.nodes.map((method) => method.id)),
    [templateFlowchart.nodes]
  );
  const compatiblePersistedPlanDesignState = isCompatiblePersistedPlanDesignState(
    persistedPlanDesignState,
    templateMethodIds
  )
    ? persistedPlanDesignState
    : undefined;
  const initialExperimentalInputs =
    compatiblePersistedPlanDesignState?.experimentalInputs ?? serverInitialExperimentalInputs;

  // always have at least 1 condition
  const serverInitialConditions: Array<Condition> = useMemo(
    () =>
      procedureRunPlan.procedureRunPlanConditions.length > 0
        ? [...procedureRunPlan.procedureRunPlanConditions]
            .sort((a, b) => a.position - b.position)
            .map((condition) => {
              const replicateCount = condition.replicates?.length ?? 0;
              return {
                id: condition.id,
                numReplicates: replicateCount > 0 ? replicateCount : 1,
                name: condition.name,
                description: condition.description ?? undefined,
              };
            })
        : [
            {
              numReplicates: 1,
              name: 'Condition 1',
              description: undefined,
            },
          ],
    [procedureRunPlan]
  );
  // Redux captures brand-new conditions before the first save assigns ids,
  // and nothing back-fills those ids into the snapshot afterwards. Refresh
  // missing ids from the server so subsequent saves update rows instead of
  // re-creating them. We match by position (index) because condition names
  // can be renamed between snapshots and are not unique-constrained.
  const initialConditions: Array<Condition> = useMemo(() => {
    if (compatiblePersistedPlanDesignState?.conditions == null) {
      return serverInitialConditions;
    }
    return compatiblePersistedPlanDesignState.conditions.map((persisted, index) => {
      if (persisted.id != null) {
        return persisted;
      }
      const serverCondition = serverInitialConditions[index];
      const serverId = serverCondition != null ? serverCondition.id : undefined;
      return serverId != null ? {...persisted, id: serverId} : persisted;
    });
  }, [compatiblePersistedPlanDesignState, serverInitialConditions]);

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null && !isInaccessible(flowchartVersion), 'Expected flowchart version');
  const procedure = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  const procedureId = procedure.id;

  // Check if a newer procedure version has been published
  const versionUpdateCheck = useProcedureVersionUpdateCheck(procedureId, flowchartVersion.id);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  // Get context to sync edit state with parent (for save-before-promote functionality)
  const {setIsInEditMode, setSavePlanIfInEditMode, setPlanErrors} = StudyOverviewContext.useContext();

  // Editable data entry mode state
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    compatiblePersistedPlanDesignState?.dataEntryMode ?? procedureRunPlan.dataEntryMode
  );
  const [initialDataEntryMode] = useState<ProcedureRunDataEntryMode>(procedureRunPlan.dataEntryMode);

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,
    context,
    methodExecutionLimitState,
    editNumColsButtons,
  } = useStudyDesignState({
    isPlan: true,
    initialExperimentalInputs,
    templateFlowchart,
    initialConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    // Only restore edit mode from a compatible persisted state. When the persisted
    // state is incompatible (e.g., template changed), start in saved mode rather than
    // falling back to context isInEditMode which may have been set from stale state.
    initialEditMode: compatiblePersistedPlanDesignState?.isInEditMode ?? false,
  });
  // Build a map from method ID to method name for error categorization
  const methodIdToName = useMemo(
    () => Object.fromEntries(templateFlowchart.nodes.map((node) => [node.id, node.name])),
    [templateFlowchart.nodes]
  );

  // Propagate validation errors to the parent context so the header can display
  // the error badge and disable the Create button
  const conditionsText = getProcedureRunConditionTextForProcedureType(procedureType, {plural: true});
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  useEffect(() => {
    const errors = collectPlanErrors({
      evaluatedValues: context.evaluatedValues,
      methodExecutionLimitState,
      conditions,
      methodIdToName,
      conditionsText,
      hasNewerVersion: versionUpdateCheck.hasNewerVersion,
      procedureString,
    });

    setPlanErrors(errors);
  }, [
    context.evaluatedValues,
    conditionsText,
    setPlanErrors,
    methodExecutionLimitState,
    conditions,
    methodIdToName,
    versionUpdateCheck.hasNewerVersion,
    procedureString,
  ]);

  // Clear plan errors only on unmount so we don't briefly flash an empty state
  // between dependency-driven re-runs of the effect above.
  useEffect(
    () => () => {
      setPlanErrors([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Create a stable save function reference that can be called from parent
  const handleSaveForPromotion = useCallback(async () => {
    if (editState !== StudyDesignTabEditState.EDITING) {
      return;
    }

    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for plan');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for plan');
    assert(context != null, 'Expected context to be non-null');

    try {
      // Build full experiment plan from current state
      const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
      const namesPerCondition = conditions.map((condition) => condition.name);
      const descriptionsPerCondition = conditions.map((condition) => condition.description);
      const experimentPlan = buildFullExperimentPlan({
        replicatesPerCondition,
        namesPerCondition,
        descriptionsPerCondition,
        numConditions: conditions.length,
        experimentalInputs,
        evaluatedValues: context.evaluatedValues,
      });

      // Serialize to plan-specific REST format (different from procedure runs)
      const conditionsForUpdate = serializePlanConditionsForNetwork(experimentPlan, conditions);

      await updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        dataEntryMode,
      });

      // Refetch to get updated data
      props.refetch();
      setEditState(StudyDesignTabEditState.SAVED);
    } catch (error) {
      // On error, go back to editing state so user can retry
      setEditState(StudyDesignTabEditState.EDITING);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editState, conditions, experimentalInputs, context, procedureRunPlan.id, props.studyId, dataEntryMode]);

  useEffect(() => {
    setIsInEditMode(editState === StudyDesignTabEditState.EDITING);
  }, [editState, setIsInEditMode]);

  useEffect(() => {
    dispatch(
      setProcedureRunPlanDesignState(props.studyId, procedureRunPlan.id, {
        conditions,
        experimentalInputs,
        dataEntryMode,
        isInEditMode: editState === StudyDesignTabEditState.EDITING,
      })
    );
  }, [
    conditions,
    dataEntryMode,
    dispatch,
    editState,
    experimentalInputs,
    procedureRunPlan.id,
    props.studyId,
  ]);

  useEffect(() => {
    setSavePlanIfInEditMode(handleSaveForPromotion);
    return () => {
      setSavePlanIfInEditMode(null);
    };
  }, [handleSaveForPromotion, setSavePlanIfInEditMode]);

  function handleCancel(): void {
    setEditState(StudyDesignTabEditState.SAVED);
    // Use resetConditionsAndInputs to atomically reset both conditions and experimental inputs
    // This avoids stale closure issues when calling setConditions and setExperimentalInputs separately
    resetConditionsAndInputs(serverInitialConditions, serverInitialExperimentalInputs);
    setDataEntryMode(initialDataEntryMode);
  }

  /**
   * Handles changing the procedure on a procedure run plan.
   * Updates the plan with the new flowchart version while preserving condition metadata
   * (names, descriptions, replicates) but clearing procedure-specific inputs.
   */
  async function handleChangeProcedure(params: ProcedureChangeParams): Promise<void> {
    // Preserve condition names, descriptions, and replicates, but clear method plans and unit op links
    // since those are specific to the old procedure
    const preservedConditions: Array<ProcedureRunPlanConditionREST> = conditions.map((condition, index) => ({
      ...(condition.id != null ? {id: condition.id} : {}),
      name: condition.name,
      description: condition.description,
      position: index,
      replicateInfos: Array.from({length: condition.numReplicates}, (_, j) => ({
        replicateNumber: j + 1,
        sourcePMEIIdsByTemplatePMDVId: {},
      })),
      methodPlans: [],
    }));

    await updateProcedureRunPlan({
      id: procedureRunPlan.id,
      studyId: props.studyId,
      // XOR validation handled by EditProcedureModal - only one of these will be set
      templateProcedureFlowchartVersionId: params.templateProcedureFlowchartVersionId,
      workflowFlowchartConfigVersionId: params.workflowFlowchartConfigVersionId,
      experimentConditions: preservedConditions,
    });

    // Exit edit mode when changing the procedure template since the new template
    // may have different methods and parameters that require fresh editing
    setEditState(StudyDesignTabEditState.SAVED);

    props.refetch();
  }

  /**
   * Compares the current and new procedure versions and preserves inputs for
   * parameters/materials/equipment whose underlying procedure elements have
   * matching stable parameter IDs (as determined by preserveProcedureInputsOnVersionUpdate).
   */
  async function handleApplyProcedureUpdate(): Promise<void> {
    if (
      isApplyingUpdate ||
      !versionUpdateCheck.hasNewerVersion ||
      !versionUpdateCheck.latestPublishedVersion
    ) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      const newFlowchart = versionUpdateCheck.latestPublishedVersion;
      const preservedInputs = preserveProcedureInputsOnVersionUpdate(
        experimentalInputs,
        templateFlowchart,
        newFlowchart,
        conditions.length
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(preservedInputs, conditions);

      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlan.id,
          studyId: props.studyId,
          templateProcedureFlowchartVersionId: versionUpdateCheck.isPoW ? undefined : newFlowchart.id,
          workflowFlowchartConfigVersionId: versionUpdateCheck.isPoW ? newFlowchart.id : undefined,
          experimentConditions: conditionsForUpdate,
        }),
        `Updating to latest ${procedureString} version...`
      );

      notifications.success(`Updated to latest ${procedureString} version.`);
      props.refetch();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.error(`Failed to apply ${procedureString} update. Please try again.`);
    } finally {
      setIsApplyingUpdate(false);
    }
  }

  async function handleOpenApplyUpdateModal(): Promise<void> {
    if (!versionUpdateCheck.hasNewerVersion || !versionUpdateCheck.latestPublishedVersion) {
      return;
    }
    if (isInaccessible(flowchartVersion)) {
      return;
    }

    const currentFlowchartVersionId = getFlowchartVersionIdForNavigation(flowchartVersion);

    await legacyRunModal(EditProcedureModal, {
      mode: EditProcedureModalMode.VERSION_UPDATE,
      procedureId,
      flowchartVersionId: currentFlowchartVersionId,
      outdatedFlowchartVersionId: currentFlowchartVersionId,
      currentFlowchartVersionId: versionUpdateCheck.latestPublishedVersion.id,
      studyType: props.studyType,
      procedureType,
      onApplyVersionUpdate: handleApplyProcedureUpdate,
    });
  }

  return selectedMethodId == null ? (
    <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
      {versionUpdateCheck.hasNewerVersion && (
        <PublishedProcedureVersionUpdateBanner
          procedureType={procedureType}
          onOpenUpdateModal={handleOpenApplyUpdateModal}
        />
      )}
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )}.`}
      />
    </div>
  ) : (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'MultiProcedureRunPlanStudyDesignTabContents', element: 'root'})}
    >
      <div className={css(studyDesignTabStyles.header)}>
        <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
        <div className={css(studyDesignTabStyles.bannerAndButtons)}>
          {versionUpdateCheck.hasNewerVersion && (
            <PublishedProcedureVersionUpdateBanner
              procedureType={procedureType}
              onOpenUpdateModal={handleOpenApplyUpdateModal}
            />
          )}
          <StudyDesignTabSavingButtons
            onCancel={handleCancel}
            onEdit={() => {
              setEditState(StudyDesignTabEditState.EDITING);
            }}
            onSave={handleSaveForPromotion}
            editState={editState}
            isSaveDisabled={!validateConditions(conditions)}
            saveDisabledTooltip={getConditionValidationTooltip(conditions) ?? undefined}
          />
        </div>
      </div>
      <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
        <FlowchartSidebar
          icons={[
            {
              name: 'note-table',
              onClick: () => {
                setSidepanelName(PARAMETER_VALUES_TAB_NAME);
              },
            },
            {
              name: 'settings',
              onClick: () => {
                setSidepanelName(SETTINGS_TAB_NAME);
              },
            },
          ]}
          selectedIconIndex={selectedDesignTabIndex}
          setSelectedIconIndex={setSelectedDesignTabIndex}
        />
        <ContextPanel
          sidepanelName={sidepanelName}
          sidepanelHeaderContent={
            selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
              ? (editNumColsButtons as JSX.Element)
              : undefined
          }
        >
          {selectedDesignTabIndex === 0 ? (
            <ParameterValuesTabContents
              methods={templateFlowchart.nodes}
              procedureFlowchart={templateFlowchart}
              selectedMethodId={selectedMethodId}
              onChangeSelectedMethod={setSelectedMethodId}
              procedureType={procedureType}
              context={context}
            />
          ) : (
            <>
              <SettingsTabContents
                conditions={conditions}
                setConditions={setConditions}
                selectedMethodId={selectedMethodId}
                experimentalInputs={experimentalInputs}
                setExperimentalInputs={setExperimentalInputs}
                locked={editState !== StudyDesignTabEditState.EDITING}
                procedureType={procedureType}
                methodExecutionLimitState={methodExecutionLimitState}
                studyType={props.studyType}
                dataEntryMode={dataEntryMode}
                estimateResult={{stale: false, executionPlan: null}}
                setDataEntryMode={setDataEntryMode}
                isPlan={true}
              />
              <SelectedProcedureEditor
                isEditable={editState === StudyDesignTabEditState.EDITING}
                procedureId={procedureId}
                flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
                procedureType={procedureType}
                studyType={props.studyType}
                onProcedureChange={handleChangeProcedure}
              />
            </>
          )}
        </ContextPanel>
      </div>
    </div>
  );
}

export default function QueryingMultiProcedureRunPlanStudyDesignTabContents({
  procedureRunPlanId,
  studyType,
  studyId,
  procedureType,
}: {
  procedureRunPlanId: string;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunPlanStudyDesignTabContentsQuery,
    MultiProcedureRunPlanStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunPlanId}});

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data, refetch} = result;
  if (isInaccessible(data.procedureRunPlan)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRunPlan}
        modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
        displayErrorText="This procedure run plan is no longer accessible."
      />
    );
  }

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(data.procedureRunPlan);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText="The design of this procedure run plan is not accessible."
      />
    );
  }

  return (
    <MultiProcedureRunPlanStudyDesignTabContents
      procedureRunPlan={data.procedureRunPlan}
      studyType={studyType}
      studyId={studyId}
      procedureType={procedureType}
      refetch={refetch}
    />
  );
}
